// Production-ready geocoding service
// Primary: ACKO Drive internal geocoding API
// Fallbacks: MapBox, BigDataCloud

import { REVERSE_GEOCODE_URL } from "./endpoints";

export interface GeocodeResult {
  city: string | null;
  country: string | null;
  state: string | null;
  pincode?: string | number | null,
  confidence: "high" | "medium" | "low";
  source: string;
}

async function geocodeWithAckoDrive(
  lat: number,
  lon: number
): Promise<GeocodeResult | null> {
  try {
    const response = await fetch(
      `${REVERSE_GEOCODE_URL}?lat=${lat}&lon=${lon}`
    );

    if (!response.ok) {
      console.warn(`ACKO Drive geocoding API returned ${response.status}`);
      return null;
    }

    const data = await response.json();

    // Validate response structure
    if (!data.city) {
      console.warn("ACKO Drive geocoding API returned no city data");
      return null;
    }

    return {
      city: data.city,
      state: data.state || null,
      country: data.country || null,
      pincode: data.pincode || null,
      confidence: data.confidence || "high",
      source: data.source || "acko_drive_internal",
    };
  } catch (error: any) {
    console.error("ACKO Drive geocoding failed:", error?.message);
    return null;
  }
}

// MapBox Geocoding (Good alternative)
async function geocodeWithMapBox(
  lat: number,
  lon: number
): Promise<GeocodeResult | null> {
  const apiKey = process.env.NEXT_PUBLIC_MAPBOX_API_KEY;

  if (!apiKey) {
    console.warn(
      "MapBox API key not found. Add NEXT_PUBLIC_MAPBOX_API_KEY to your .env.local"
    );
    return null;
  }

  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${apiKey}&language=en&types=place`
    );

    if (!response.ok) return null;

    const data = await response.json();

    if (!data.features?.length) return null;

    const feature = data.features[0];
    const city = feature.text;

    // Extract state and country from context
    const context = feature.context || [];
    const state =
      context.find((c: any) => c.id.startsWith("region"))?.text || null;
    const country =
      context.find((c: any) => c.id.startsWith("country"))?.text || null;

    return {
      city,
      state,
      country,
      confidence: "high",
      source: "mapbox",
    };
  } catch (error: any) {
    console.error("MapBox geocoding failed:", error?.message);
    return null;
  }
}

// Free fallback API
async function geocodeWithBigDataCloud(
  lat: number,
  lon: number
): Promise<GeocodeResult | null> {
  try {
    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`
    );

    if (!response.ok) return null;

    const data = await response.json();
    const city = data.city || data.locality || null;

    return {
      city,
      state: data.principalSubdivision || null,
      country: data.countryName || null,
      confidence: "medium",
      source: "bigdatacloud",
    };
  } catch (error: any) {
    console.error("BigDataCloud geocoding failed:", error?.message);
    return null;
  }
}

// Main geocoding function with fallbacks
export async function reverseGeocode(
  lat: number,
  lon: number
): Promise<GeocodeResult | null> {
  let result = await geocodeWithAckoDrive(lat, lon);
  if (result?.city) {
    return result;
  }

  // Try MapBox as fallback
  result = await geocodeWithMapBox(lat, lon);
  if (result?.city) {
    console.log(`✅ MapBox success: ${result.city}`);
    return result;
  }

  // Try BigDataCloud as final fallback
  result = await geocodeWithBigDataCloud(lat, lon);
  if (result?.city) {
    console.log(`✅ BigDataCloud success: ${result.city}`);
    return result;
  }

  console.error("🚫 All geocoding services failed");
  return null;
}

// Legacy function for backward compatibility
export async function reverseGeocodeCity(
  lat: number,
  lon: number
): Promise<string | null> {
  const result = await reverseGeocode(lat, lon);
  return result?.city || null;
}
